import { gql } from "@apollo/client";

export default gql`
  fragment ComplianceProgramReportPrevAnswer on MostRecentAssessmentAnswerNode {
    questionId
    questionType
    integerAnswer
    submitted
  }
  fragment ComplianceProgramReportAnswer on AssessmentAnswerInterface {
    id
    hasCalibrations
    hasCalibrationSuggestions
    ... on RangeAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on IndividualGoalAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on OverallGoalAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on IndividualRecognitionCoreValueAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on OverallRecognitionCoreValueAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on ResponsibilityAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
      roleId
    }
    ... on TalentIndicatorAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on CompetencyAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
      roleId
    }
    ... on CompetencyCriteriaAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
      roleId
    }
    ... on MultiChoiceAssessmentAnswerNode {
      id
      questionId
      choices
      comment
    }
    ... on TextAssessmentAnswerNode {
      id
      questionId
      textAnswer
      comment
    }
    ... on NpsAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
  }
  fragment ComplianceProgramReportAssessment on AssessmentNode {
    id
    state
    responder {
      id
      name
      firstName
      avatar
    }
    target {
      ...ComplianceProgramTarget
    }
    mostRecentAnswers {
      ...ComplianceProgramReportPrevAnswer
    }
    answers(
      questionType: [
        range
        talent_indicator
        overall_goal
        overall_recognition_core_value
      ]
    ) {
      ...ComplianceProgramReportAnswer
    }
    roleAssessmentAvg
    selfRoleAssessmentAvg
    assessmentDelivery {
      id
      state
      canUpdate {
        permission
      }
      deliveryDatetime
      canRead {
        permission
      }
    }
    template {
      id
      organization {
        id
      }
    }
    assessmentDeliveryState
  }
  fragment ComplianceProgramReportQuestion on AssessmentQuestionInterface {
    ... on RangeAssessmentQuestionNode {
      id
      title
      description
      startValue
      endValue
      labels
      questionType
      responses
      labelDescriptions
      comment
    }
    ... on CompetencyAssessmentQuestionNode {
      id
      title
      description
      startValue
      endValue
      labels
      questionType
      responses
      labelDescriptions
      comment
    }
    ... on IndividualGoalAssessmentQuestionNode {
      id
      questionType
    }
    ... on OverallGoalAssessmentQuestionNode {
      id
      title
      description
      startValue
      endValue
      labels
      questionType
      responses
      labelDescriptions
      comment
    }
    ... on IndividualRecognitionCoreValueAssessmentQuestionNode {
      id
      questionType
    }
    ... on OverallRecognitionCoreValueAssessmentQuestionNode {
      id
      title
      description
      startValue
      endValue
      labels
      questionType
      responses
      labelDescriptions
      comment
    }
    ... on CompetencyCriteriaAssessmentQuestionNode {
      id
      title
      description
      startValue
      endValue
      labels
      questionType
      responses
      labelDescriptions
      comment
    }
    ... on ResponsibilityAssessmentQuestionNode {
      id
      title
      description
      startValue
      endValue
      labels
      questionType
      responses
      labelDescriptions
      comment
    }
    ... on TalentIndicatorAssessmentQuestionNode {
      id
      title
      description
      startValue
      endValue
      labels
      questionType
      responses
      labelDescriptions
      comment
      talentIndicator {
        id
        name
        customName
        talentIndicatorType
      }
    }
    ... on MultiChoiceAssessmentQuestionNode {
      id
      title
      description
      options
      questionType
      responses
      comment
    }
    ... on TextAssessmentQuestionNode {
      id
      title
      description
      questionType
      responses
      comment
    }
    ... on NpsAssessmentQuestionNode {
      id
      title
      description
      questionType
      responses
      comment
    }
  }
  fragment ComplianceProgramTarget on UserNode {
    id
    name
    avatar
    organizationSpecificInfo {
      startDate
    }
    primaryManager {
      id
      name
    }
    teamsList {
      id
      title
    }
  }

  fragment ComplianceProgramForReportAssessmentGroupSection on AssessmentGroupSectionNode {
    id
    title
    description
    questions {
      edges {
        node {
          id
          question {
            ...ComplianceProgramReportQuestion
          }
        }
      }
    }
  }

  fragment ComplianceProgramForReportAssessmentGroup on AssessmentGroupNode {
    id
    title
    sections {
      edges {
        node {
          ...ComplianceProgramForReportAssessmentGroupSection
        }
      }
    }
  }

  fragment EngagementSurveyReportRow on EngagementSurveyReportRowNode {
    title
    question {
      ...ComplianceProgramReportQuestion
    }
    rollups {
      edges {
        node {
          rollupType
          team {
            id
            title
          }
          manager {
            id
            name
            firstName
            avatar
          }
          careerTrack {
            id
            title
          }
          isManager
          value
        }
      }
    }
  }

  fragment EngagementSurveyReportResponder on EngagementSurveyReportRespondersNode {
    rollupType
    team {
      id
      title
    }
    manager {
      id
      name
      firstName
      avatar
    }
    careerTrack {
      id
      title
    }
    isManager
    responderCount
    isHidden
  }

  fragment ComplianceProgramPerformanceReportRow on PerformanceReviewReportRowNode {
    id
    user {
      ...ComplianceProgramTarget
    }
    targetAddedToProgramOn
    position
    calibrationStatus
    roles {
      id
      title
      level
      careerTrack {
        id
        title
      }
    }
    assessment {
      ...ComplianceProgramReportAssessment
    }
  }

  fragment ComplianceProgramManagerEffectivenessReportRow on ManagerEffectivenessReportRowNode {
    manager {
      ...ComplianceProgramTarget
      directReports {
        totalCount
      }
    }
    assessments {
      totalCount
    }
    assessmentDelivery {
      id
    }
    questions {
      edges {
        node {
          question {
            id
          }
          avgAnswer
        }
      }
    }
  }

  query getComplianceProgramForReport(
    $complianceProgramId: Int!
    $isUpwardAssessmentReport: Boolean!
    $isPerformanceAssessmentReport: Boolean!
    $isEngagementSurveyReport: Boolean!
    $engagementSurveyReportGroupBy: EngagementSurveyReportGroupBy!
    $engagementSurveyReportTeamIds: [Int]
    $engagementSurveyReportManagerIds: [Int]
  ) {
    complianceProgram(pk: $complianceProgramId) {
      id
      title
      periodStartDate
      periodEndDate
      hasAccessToAppliesToUsers
      ongoing
      calibration
      assessmentDeliveries @include(if: $isEngagementSurveyReport) {
        edges {
          node {
            id
          }
        }
      }
      performanceAssessmentTemplate
        @include(if: $isPerformanceAssessmentReport) {
        id
        assessmentType
        isOnlySelfAssessment
        delivery
        enableGoalAssessments
        goalAssessmentScale
        goalAssessmentScaleLabels
        goalAssessmentScaleStartsAt
        enableCoreValueAssessments
        coreValueAssessmentScale
        coreValueAssessmentScaleLabels
        coreValueAssessmentScaleStartsAt
        enableRoleBasedAssessments
        roleBasedAssessmentScale
        roleBasedAssessmentScaleLabels
        roleBasedAssessmentScaleStartsAt
        questionSets {
          edges {
            node {
              ...ComplianceProgramForReportAssessmentGroup
            }
          }
        }
        additionalQuestions {
          ... on OverallGoalAssessmentQuestionNode {
            id
            title
            description
            startValue
            endValue
            labels
            questionType
            responses
            labelDescriptions
            comment
          }
          ... on OverallRecognitionCoreValueAssessmentQuestionNode {
            id
            title
            description
            startValue
            endValue
            labels
            questionType
            responses
            labelDescriptions
            comment
          }
          ... on RangeAssessmentQuestionNode {
            id
          }
          ... on CompetencyAssessmentQuestionNode {
            id
          }
          ... on IndividualGoalAssessmentQuestionNode {
            id
          }
          ... on IndividualRecognitionCoreValueAssessmentQuestionNode {
            id
          }
          ... on CompetencyCriteriaAssessmentQuestionNode {
            id
          }
          ... on ResponsibilityAssessmentQuestionNode {
            id
          }
          ... on TalentIndicatorAssessmentQuestionNode {
            id
          }
          ... on MultiChoiceAssessmentQuestionNode {
            id
          }
          ... on TextAssessmentQuestionNode {
            id
          }
          ... on NpsAssessmentQuestionNode {
            id
          }
        }
      }
      managerAssessmentTemplate @include(if: $isUpwardAssessmentReport) {
        id
        assessmentType
        isOnlySelfAssessment
        delivery
        enableGoalAssessments
        goalAssessmentScale
        goalAssessmentScaleLabels
        goalAssessmentScaleStartsAt
        enableCoreValueAssessments
        coreValueAssessmentScale
        coreValueAssessmentScaleLabels
        coreValueAssessmentScaleStartsAt
        enableRoleBasedAssessments
        roleBasedAssessmentScale
        roleBasedAssessmentScaleLabels
        roleBasedAssessmentScaleStartsAt
        questionSets {
          edges {
            node {
              ...ComplianceProgramForReportAssessmentGroup
            }
          }
        }
        additionalQuestions {
          ... on OverallGoalAssessmentQuestionNode {
            id
            title
            description
            startValue
            endValue
            labels
            questionType
            responses
            labelDescriptions
            comment
          }
          ... on OverallRecognitionCoreValueAssessmentQuestionNode {
            id
            title
            description
            startValue
            endValue
            labels
            questionType
            responses
            labelDescriptions
            comment
          }
          ... on RangeAssessmentQuestionNode {
            id
          }
          ... on CompetencyAssessmentQuestionNode {
            id
          }
          ... on IndividualGoalAssessmentQuestionNode {
            id
          }
          ... on IndividualRecognitionCoreValueAssessmentQuestionNode {
            id
          }
          ... on CompetencyCriteriaAssessmentQuestionNode {
            id
          }
          ... on ResponsibilityAssessmentQuestionNode {
            id
          }
          ... on TalentIndicatorAssessmentQuestionNode {
            id
          }
          ... on MultiChoiceAssessmentQuestionNode {
            id
          }
          ... on TextAssessmentQuestionNode {
            id
          }
          ... on NpsAssessmentQuestionNode {
            id
          }
        }
      }
      performanceReportRows @include(if: $isPerformanceAssessmentReport) {
        edges {
          node {
            ...ComplianceProgramPerformanceReportRow
          }
        }
      }
      managerEffectivenessReport @include(if: $isUpwardAssessmentReport) {
        edges {
          node {
            ...ComplianceProgramManagerEffectivenessReportRow
          }
        }
      }
      engagementSurveyReport(
        groupBy: $engagementSurveyReportGroupBy
        teamIds: $engagementSurveyReportTeamIds
        managerIds: $engagementSurveyReportManagerIds
      ) @include(if: $isEngagementSurveyReport) {
        responders {
          edges {
            node {
              ...EngagementSurveyReportResponder
            }
          }
        }
        rangeQuestionRows {
          edges {
            node {
              ...EngagementSurveyReportRow
            }
          }
        }
        npsQuestionRows {
          edges {
            node {
              ...EngagementSurveyReportRow
            }
          }
        }
      }
    }
  }
`;
